/**
 * useEditGuard — ScheduleDraft 编辑器离开保护与键盘快捷键。
 *
 * 本 hook 提供三层保护：
 * 1. 浏览器原生 beforeunload（关闭/刷新标签页）
 * 2. React Router navigation blocker / link fallback（应用内路由跳转）
 * 3. 键盘快捷键注册（Ctrl/Cmd+Z/Shift+Z/S）
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md §8.3, §12
 * 骨架关联 → hooks/useLocalEditState.js (dirty state),
 *             hooks/useSaveLocalEdits.js (保存流程),
 *             pages/editor/ScheduleDraftEditorPage.jsx (挂载点)
 */

import { useCallback, useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";

const LEAVE_WARNING = "您有未保存的本地航班或 Compare 修改，确定要离开吗？";

function isModifiedClick(event) {
    return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0;
}

function shouldHandleAnchorNavigation(anchor) {
    if (!anchor?.href || anchor.target && anchor.target !== "_self") {
        return false;
    }
    const nextUrl = new URL(anchor.href, window.location.href);
    return nextUrl.origin === window.location.origin && nextUrl.href !== window.location.href;
}

function useRouteLeaveGuard(isDirty) {
    const navigationContext = useContext(NavigationContext);
    const navigator = navigationContext?.navigator;
    const isDirtyRef = useRef(isDirty);
    const skipNextNavigationPromptRef = useRef(false);

    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);

    useEffect(() => {
        if (!isDirty || typeof navigator?.block !== "function") {
            return undefined;
        }

        const unblock = navigator.block((transition) => {
            if (!isDirtyRef.current || window.confirm(LEAVE_WARNING)) {
                unblock();
                transition.retry();
            }
        });

        return unblock;
    }, [isDirty, navigator]);

    useEffect(() => {
        if (!isDirty || typeof navigator?.block === "function" || typeof navigator?.push !== "function") {
            return undefined;
        }

        const originalPush = navigator.push;
        const originalReplace = navigator.replace;
        const confirmNavigation = () => {
            if (!isDirtyRef.current) {
                return true;
            }
            if (skipNextNavigationPromptRef.current) {
                skipNextNavigationPromptRef.current = false;
                return true;
            }
            return window.confirm(LEAVE_WARNING);
        };

        navigator.push = (...args) => {
            if (confirmNavigation()) {
                return originalPush.apply(navigator, args);
            }
            return undefined;
        };

        if (typeof originalReplace === "function") {
            navigator.replace = (...args) => {
                if (confirmNavigation()) {
                    return originalReplace.apply(navigator, args);
                }
                return undefined;
            };
        }

        return () => {
            navigator.push = originalPush;
            if (typeof originalReplace === "function") {
                navigator.replace = originalReplace;
            }
        };
    }, [isDirty, navigator]);

    useEffect(() => {
        if (!isDirty || typeof navigator?.block === "function") {
            return undefined;
        }

        const handleDocumentClick = (event) => {
            if (event.defaultPrevented || isModifiedClick(event)) {
                return;
            }

            const anchor = event.target?.closest?.("a[href]");
            if (!shouldHandleAnchorNavigation(anchor)) {
                return;
            }

            if (!window.confirm(LEAVE_WARNING)) {
                event.preventDefault();
                event.stopPropagation();
            } else {
                skipNextNavigationPromptRef.current = true;
            }
        };

        document.addEventListener("click", handleDocumentClick, true);
        return () => document.removeEventListener("click", handleDocumentClick, true);
    }, [isDirty, navigator]);
}

/**
 * @typedef {Object} EditGuardOptions
 * @property {boolean} isDirty 是否有未保存的修改。
 * @property {() => void} onUndo Undo 回调。
 * @property {() => void} onRedo Redo 回调。
 * @property {() => Promise<void>} onSave 保存回调。
 * @property {boolean} canUndo 是否可 undo。
 * @property {boolean} canRedo 是否可 redo。
 * @property {boolean} shortcutsDisabled 是否禁用编辑快捷键。
 */

/**
 * 注册离开保护和键盘快捷键。
 *
 * @param {EditGuardOptions} options
 */
export function useEditGuard({ isDirty, onUndo, onRedo, onSave, canUndo, canRedo, shortcutsDisabled = false }) {
    useRouteLeaveGuard(isDirty);

    // Layer 1: Browser beforeunload
    useEffect(() => {
        if (!isDirty) {
            return;
        }

        const handler = (event) => {
            event.preventDefault();
            // Modern browsers ignore custom messages but require returnValue
            event.returnValue = LEAVE_WARNING;
        };

        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    // Layer 3: Keyboard shortcuts
    const handleKeyDown = useCallback(
        (event) => {
            if (shortcutsDisabled) {
                return;
            }

            const isMod = event.metaKey || event.ctrlKey;
            if (!isMod) {
                return;
            }

            // Ignore when focus is inside input/textarea/contenteditable
            const tag = event.target?.tagName;
            const isEditable = event.target?.isContentEditable;
            if (tag === "INPUT" || tag === "TEXTAREA" || isEditable) {
                // Allow Ctrl+Z/S in input fields to work natively when not dirty
                // But intercept for our undo/save when dirty
                if (!isDirty && event.key !== "s") {
                    return;
                }
            }

            switch (event.key.toLowerCase()) {
                case "z": {
                    event.preventDefault();
                    if (event.shiftKey) {
                        if (canRedo) {
                            onRedo?.();
                        }
                    } else if (canUndo) {
                        onUndo?.();
                    }
                    break;
                }

                case "s": {
                    event.preventDefault();
                    if (isDirty) {
                        onSave?.();
                    }
                    break;
                }

                default:
                    break;
            }
        },
        [canRedo, canUndo, isDirty, onRedo, onSave, onUndo, shortcutsDisabled],
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);
}

export default useEditGuard;
